import React from 'react'
import styled from '@emotion/styled'

export interface MultiButtonProps {
  'aria-label'?: string
  'aria-pressed'?: boolean
  id?: string
  className?: string
  title?: string
  disabled?: boolean
  canFocus?: boolean
  visible?: boolean
  href?: string
  download?: string | boolean
  children?: React.ReactNode
  onClick?: (evt: React.MouseEvent<HTMLElement>) => unknown
  dataTestId?: string
}

const StyledButton = styled.button<MultiButtonProps>`
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  pointer-events: ${props => (props.disabled ? 'none' : 'inherit')};
  outline: none;
  user-select: none;
  visibility: ${props => (props.visible ? 'visible' : 'hidden')};
  &,
  &:hover {
    text-decoration: none;
  }
`

const StyledAnchor = styled.a<MultiButtonProps>`
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  pointer-events: ${props => (props.disabled ? 'none' : 'inherit')};
  outline: none;
  visibility: ${props => (props.visible ? 'visible' : 'hidden')};
  &,
  &:hover,
  &:visited {
    text-decoration: none;
  }
`

export const MultiButton = React.forwardRef<HTMLElement, MultiButtonProps>((props, ref) => {
  const {
    'aria-label': ariaLabel,
    'aria-pressed': ariaPressed,
    id,
    className,
    title,
    children,
    disabled,
    visible,
    canFocus,
    href,
    onClick,
    download,
    dataTestId
  } = props

  if (href) {
    return (
      <StyledAnchor
        id={id}
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={className}
        title={title}
        aria-pressed={ariaPressed}
        href={disabled ? 'javascript:void(0)' : href}
        visible={visible !== false}
        download={download}
        rel="noopener"
        onMouseDown={canFocus === false ? preventDefault : undefined}
        tabIndex={canFocus === false ? -1 : 0}>
        {children}
      </StyledAnchor>
    )
  } else if (onClick) {
    return (
      <StyledButton
        data-test-id={dataTestId}
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        id={id}
        ref={ref as React.Ref<HTMLButtonElement>}
        className={className}
        title={title}
        disabled={disabled}
        visible={visible !== false}
        onClick={onClick}
        onMouseDown={canFocus === false ? preventDefault : undefined}
        tabIndex={canFocus === false ? -1 : undefined}>
        {children}
      </StyledButton>
    )
  } else {
    throw new Error('MultiButton must specify href or onClick')
  }

  function preventDefault(evt: React.MouseEvent) {
    /* Doing this on mouseDown stops the element from
       stealing focus away from other elements when clicked */
    evt.preventDefault()
  }
})
